import { useNavigate } from 'react-router-dom';
import type { CohortDistribution } from '@/lib/types';

interface DistributionCardProps {
  distribution: CohortDistribution;
}

type Kind = 'ok' | 'warn' | 'crit';

const STATUS_LABEL: Record<Kind, string> = {
  ok: 'Óptimo',
  warn: 'Mejorable',
  crit: 'Prioritario',
};

const DOT_BG: Record<Kind, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
};

const COUNT_COLOR: Record<Kind, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

const SEG_BG: Record<Kind, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
};

export function DistributionCard({ distribution }: DistributionCardProps) {
  const navigate = useNavigate();
  const goToFilter = (filter: Kind) => {
    navigate(`/patients?status=${filter}`);
  };

  const total =
    distribution.ok.count + distribution.warn.count + distribution.crit.count;

  return (
    <div className="glass-panel p-5">
      <header className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] font-semibold tracking-wider uppercase text-muted m-0">
          Distribución por status
        </h3>
        <span className="text-[11px] text-tertiary tnum">
          {total} pacientes
        </span>
      </header>

      <div className="flex h-1.5 gap-px rounded-full overflow-hidden mb-5">
        {(['ok', 'warn', 'crit'] as const).map((kind) => (
          <div
            key={kind}
            className={`h-full ${SEG_BG[kind]} first:rounded-l-full last:rounded-r-full`}
            style={{ width: `${distribution[kind].pct}%` }}
            aria-label={`${STATUS_LABEL[kind]} ${distribution[kind].pct}%`}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {(['ok', 'warn', 'crit'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => goToFilter(kind)}
            className="group flex flex-col gap-1.5 bg-transparent border border-line rounded-md px-3 py-2.5 font-inherit text-left cursor-pointer transition-colors hover:bg-soft hover:border-line-default"
          >
            <div className="flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted group-hover:text-tertiary">
              <span className={`w-1.5 h-1.5 rounded-full ${DOT_BG[kind]}`} aria-hidden="true" />
              {STATUS_LABEL[kind]}
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-2xl font-bold tracking-[-0.025em] tnum leading-none ${COUNT_COLOR[kind]}`}>
                {distribution[kind].count}
              </span>
              <span className="text-[11px] text-tertiary tnum">
                {distribution[kind].pct}%
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
